/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect } from 'react';
import {
  Controller, FieldValues, SubmitHandler,
} from 'react-hook-form';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Alert,
  AlertTitle,
  TextField,
  Box,
  Button,
  Stack,
  FormHelperText,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { PartnerCountry, TripMatch } from '@marageti/z4-sdk/lib/travel';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import {
  MultiCountrySelector,
  useForm,
  getFieldProps,
  getFormProps,
  getSubmitButtonProps,
} from '@marageti/z4-lib';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { addYears } from 'date-fns';
import {
  TripEditSchema,
} from './trip-match-edit-form-util';
import CurrencyInput from '../../../components/currency-input';

export interface CountryObject {
  country: string
}

type TripEditProps = {
  countries: PartnerCountry[];
  onCancel: () => void;
  onSubmit: SubmitHandler<FieldValues>;
  submissionError?: ApiError;
  tripMatch?: TripMatch;
};

const TripMatchEditForm = ({
  countries,
  onCancel,
  onSubmit,
  submissionError = undefined,
  tripMatch = undefined,
}: TripEditProps) => {
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isValid },
    register,
    trigger,
  } = useForm({
    defaultValues: {
      ...tripMatch,
      departureDate: tripMatch?.departureDate ? new Date(tripMatch.departureDate) : undefined,
      budget: tripMatch?.budget ? tripMatch.budget : undefined,
      salesStage: tripMatch?.salesStage || 'Undefined',
    },
    resolver: zodResolver(TripEditSchema),
  });

  useEffect(() => {
    trigger();
  }, []);

  const maxDate = addYears(new Date(), 10);

  return (
    <form name="trip-match-form" {...getFormProps(handleSubmit(onSubmit))}>
      <Stack spacing={3}>
        <Controller
          name="countries"
          control={control}
          render={({ field }) => (
            <MultiCountrySelector
              {...field}
              countries={countries}
              label="Countries"
              onChange={(_: any, data: String[]) => field.onChange(data)}
              {...getFieldProps('countries', errors)}
            />
          )}
        />
        <Box>
          <Stack direction="row" spacing={2}>
            <Controller
              name="departureDate"
              control={control}
              render={({
                field: {
                  onChange, onBlur, value, ref, name,
                },
              }) => (
                <DatePicker
                  label="Departure Date (MM/DD/YYYY)"
                  onChange={onChange}
                  maxDate={maxDate}
                  value={value}
                  slotProps={{
                    textField: {
                      name, onBlur, error: !!errors.departureDate, sx: { width: '70%' },
                    },
                  }}
                  inputRef={ref}
                />
              )}
            />
            <TextField
              type="number"
              label="Nights"
              sx={{ flex: 1 }}
              inputProps={{ min: 5 }}
              {...register('totalNights', { valueAsNumber: true })}
              {...getFieldProps('totalNights', errors)}
            />
          </Stack>
          {errors.departureDate && <FormHelperText error>{errors.departureDate.message as string}</FormHelperText>}
          {errors.totalNights && <FormHelperText error>{errors.totalNights.message as string}</FormHelperText>}
        </Box>
        <Controller
          control={control}
          name="budget"
          render={({ field }) => (
            <CurrencyInput
              label="Total Budget"
              fullWidth
              required
              {...field}
              {...getFieldProps('budget', errors)}
              defaultValue={tripMatch?.budget}
              numericFormatProps={{
                allowNegative: false,
                decimalScale: 0,
              }}
            />
          )}
        />
        <Stack direction="row" spacing={2}>
          <TextField
            type="number"
            label="Adults"
            sx={{ flex: 1 }}
            inputProps={{ min: 0 }}
            {...register('numberOfAdults', { valueAsNumber: true })}
            {...getFieldProps('numberOfAdults', errors)}
          />
          <TextField
            type="number"
            label="Children"
            sx={{ flex: 1 }}
            inputProps={{ min: 0 }}
            {...register('numberOfChildren', { valueAsNumber: true })}
            {...getFieldProps('numberOfChildren', errors)}
          />
        </Stack>

        {submissionError && (
          <Alert severity="error">
            <AlertTitle>There was an error on our end.</AlertTitle>
            {`Code: ${submissionError.code}: ${submissionError.message}`}
            <br />
            {submissionError.id && `ID: ${submissionError.id}`}
          </Alert>
        )}
      </Stack>
      <Stack direction="row" spacing={2} mt="44px">
        <LoadingButton
          variant="contained"
          type="submit"
          {...getSubmitButtonProps(isSubmitting, isValid)}
        >
          Save
        </LoadingButton>
        <Button variant="outlined" onClick={onCancel}>Cancel</Button>
      </Stack>
    </form>
  );
};

export default TripMatchEditForm;
